import React, { createContext, useContext, useEffect, useState, useRef } from 'react'
// MIGRATION: Replace direct Supabase import with secure API client
import authAPI from '../lib/authAPI'
// Centralized API configuration for Supabase backend
import { adminAPI } from '../lib/api'
// Keep Supabase client for database operations only (not auth)
import { supabase, executeQuery, checkConnectionHealth, checkConnectionHealthDirect, getConnectionState, getConnectionMetrics, resetCircuitBreaker, testRawSupabaseClient } from '../lib/supabase'
// Session management for timeouts and token refresh
import sessionManager from '../lib/sessionManager'
import { clearBrowserSessionState, failClosedBrowserAuthority, onBrowserSessionInvalidated, reconcileBrowserIdentity, shouldPreserveCustomAuthorityOnDirectSignOut, terminateBrowserAuthority } from '../services/browserState'

const AuthContext = createContext({})

export const useAuth = () => {
  return useContext(AuthContext)
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isSigningOut, setIsSigningOut] = useState(false)
  const isSigningOutRef = useRef(false)
  const profileOperationInProgress = useRef(false)
  const profileOperationTimeoutRef = useRef(null)
  const [dbConnectionHealthy, setDbConnectionHealthy] = useState(true)
  const previousUserIdRef = useRef(null)

  const clearPriorUserBeforeSwitch = nextUser => {
    if (previousUserIdRef.current && nextUser?.id && previousUserIdRef.current !== nextUser.id) {
      clearBrowserSessionState({ reason: 'user-switch', preserveAuthority: true })
    }
    previousUserIdRef.current = nextUser?.id || null
  }

  useEffect(() => onBrowserSessionInvalidated((_reason, { preserveAuthority } = {}) => {
    if (!preserveAuthority) {
      void terminateBrowserAuthority({
        authClient: authAPI,
        directClient: supabase,
        manager: sessionManager,
        failClosed: failClosedBrowserAuthority,
      })
    }
    setUser(null)
    setUserProfile(null)
    setLoading(false)
  }), [])

  useEffect(() => {
    if (previousUserIdRef.current && user?.id && previousUserIdRef.current !== user.id) {
      clearBrowserSessionState({ reason: 'user-switch', preserveAuthority: true })
    }
    previousUserIdRef.current = user?.id || null
  }, [user])
  
  // Lazy loading flag for profile
  const profileLoadingRef = useRef(false)
  
  // CRITICAL FALLBACK: Auto-approve email confirmed users
  const autoApproveEmailConfirmedUser = async (profile) => {
    try {
      // Check if user email is confirmed but profile is still pending
      const isEmailConfirmed = user?.email_confirmed_at != null
      const isPending = profile?.status === 'pending'
      
      if (isEmailConfirmed && isPending) {
        console.log('🔄 AuthContext: FALLBACK ACTIVATION - Email confirmed but profile pending, auto-approving...')
        console.log('🔄 AuthContext: User email confirmed at:', user.email_confirmed_at)
        console.log('🔄 AuthContext: Current profile status:', profile.status)
        
        // Use direct Supabase update with service role permissions
        if (supabase) {
          const { data: updatedProfile, error: updateError } = await supabase
            .from('user_profiles')
            .update({ 
              status: 'approved'
            })
            .eq('user_id', user.id)
            .eq('status', 'pending') // Only update if still pending
            .select()
            .maybeSingle()

          if (updateError) {
            console.error('🔄 AuthContext: FALLBACK failed to update profile:', updateError)
            // Return original profile if update fails
            return profile
          }

          if (updatedProfile) {
            console.log('🔄 AuthContext: FALLBACK SUCCESS - Profile auto-approved:', updatedProfile)
            return updatedProfile
          } else {
            console.log('🔄 AuthContext: FALLBACK - No profile updated (may have been updated already)')
            return profile
          }
        } else {
          console.log('🔄 AuthContext: FALLBACK - Supabase client not available, cannot auto-approve')
          return profile
        }
      }
      
      return profile
    } catch (error) {
      console.error('🔄 AuthContext: FALLBACK error during auto-approval:', error)
      return profile // Return original profile on error
    }
  }

  // Lazy load user profile when needed
  const ensureUserProfile = async () => {
    if (!user || userProfile || profileLoadingRef.current) {
      return userProfile
    }
    
    profileLoadingRef.current = true
    
    try {
      console.log('🔍 AuthContext: Lazy loading user profile...')
      let profile = await fetchUserProfile(user.id)
      
      // Create profile if it doesn't exist
      if (!profile) {
        console.log('🔍 AuthContext: Profile not found, creating new one...')
        profile = await createUserProfile(user)
      }
      
      // CRITICAL FALLBACK: Auto-approve confirmed users with pending status
      if (profile) {
        profile = await autoApproveEmailConfirmedUser(profile)
      }
      
      console.log('🔍 AuthContext: Profile loaded:', profile)
      setUserProfile(profile)
      return profile
    } catch (error) {
      console.error('🔍 AuthContext: Error loading profile:', error)
      setUserProfile(null)
      return null
    } finally {
      profileLoadingRef.current = false
    }
  }

  // Helper function to safely start profile operation with timeout protection
  const startProfileOperation = (context) => {
    // Clear any existing timeout
    if (profileOperationTimeoutRef.current) {
      clearTimeout(profileOperationTimeoutRef.current)
    }
    
    profileOperationInProgress.current = true
    console.log(`🔍 AuthContext: Profile operation started (${context})`)
    
    // Set emergency timeout to prevent stuck operations (30 seconds)
    profileOperationTimeoutRef.current = setTimeout(() => {
      console.error('🔍 AuthContext: EMERGENCY - Profile operation stuck, forcing reset')
      profileOperationInProgress.current = false
      profileOperationTimeoutRef.current = null
    }, 30000)
  }

  // Helper function to safely end profile operation
  const endProfileOperation = (context) => {
    if (profileOperationTimeoutRef.current) {
      clearTimeout(profileOperationTimeoutRef.current)
      profileOperationTimeoutRef.current = null
    }
    
    profileOperationInProgress.current = false
    console.log(`🔍 AuthContext: Profile operation ended (${context})`)
  }

  // Use the enhanced database health check from the Supabase client
  const checkDatabaseHealthWithState = async () => {
    try {
      console.log('🔍 AuthContext: Delegating to enhanced health check...')
      const isHealthy = await checkConnectionHealth()
      setDbConnectionHealthy(isHealthy)
      return isHealthy
    } catch (error) {
      console.error('🔍 AuthContext: Health check delegation failed:', error)
      setDbConnectionHealthy(false)
      return false
    }
  }

  // Enhanced database query wrapper using the new executeQuery with circuit breaker
  const executeQueryWithState = async (queryFn, operation, options = {}) => {
    try {
      const result = await executeQuery(queryFn, operation, {
        // No need to specify timeout here - executeQuery will use environment-specific defaults
        retries: 2,
        retryDelay: 1000,
        ...options // Allow override of defaults
      })
      
      // Update local state based on success
      setDbConnectionHealthy(true)
      return result
    } catch (error) {
      // Update local state based on failure
      const connectionState = getConnectionState()
      setDbConnectionHealthy(connectionState.healthy)
      
      throw error
    }
  }

  // Fetch user profile with APPLICATION-LEVEL security (no RLS)
  const fetchUserProfile = async (userId) => {
    try {
      console.log(`🔍 AuthContext: Fetching profile for userId: ${userId}`)
      console.log('🔍 AuthContext: Supabase client status:', {
        exists: !!supabase,
        hasReactAppUrl: !!process.env.REACT_APP_SUPABASE_URL,
        hasReactAppKey: !!process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY,
        clientType: typeof supabase,
        nodeEnv: process.env.NODE_ENV
      })
      
      // Check if Supabase is available for database operations
      if (!supabase) {
        console.log('🔍 AuthContext: Supabase not available - profile operations disabled in production')
        return null
      }
      
      // APPLICATION SECURITY: Check if user can access this profile
      const currentUserId = user?.id
      const currentUserEmail = user?.email
      const isBreakGlassAdmin = currentUserEmail === 'stephen@seckar.com.au'
      
      // Allow access if:
      // 1. User is fetching their own profile
      // 2. User is break-glass admin
      if (!isBreakGlassAdmin && currentUserId !== userId) {
        console.log('🔍 AuthContext: Access denied - user can only access own profile')
        throw new Error('Access denied: You can only access your own profile')
      }
      
      // Direct query without RLS (RLS is disabled)
      console.log('🔍 AuthContext: Executing Supabase query for user_profiles...')
      const queryStart = Date.now()
      
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle()

      const queryDuration = Date.now() - queryStart
      console.log(`🔍 AuthContext: Query completed in ${queryDuration}ms`)

      if (error) {
        console.error('🔍 AuthContext: Error fetching user profile:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        })
        
        // If user doesn't exist, that's okay - we'll create one
        if (error.code === 'PGRST116') {
          console.log('🔍 AuthContext: User profile does not exist, will create one')
          return null
        }
        
        // For other errors, return null to trigger profile creation
        console.log('🔍 AuthContext: Profile fetch failed, will attempt to create profile')
        return null
      }

      if (data) {
        console.log('🔍 AuthContext: Successfully fetched profile:', data)
        return data
      } else {
        console.log('🔍 AuthContext: No profile found (data is null), will create one')
        return null
      }
    } catch (error) {
      console.error('🔍 AuthContext: Exception fetching user profile:', error)
      
      // Return null to trigger profile creation as fallback
      return null
    }
  }

  // Create user profile - try Edge Function first, fallback to virtual profile
  const createUserProfile = async (user) => {
    try {
      console.log('🔍 AuthContext: Creating profile for user:', user.email)
      
      // IMMEDIATE WORKAROUND: For break-glass admin, create virtual profile first
      const isBreakGlassAdmin = user.email === 'stephen@seckar.com.au'
      if (isBreakGlassAdmin) {
        console.log('🔍 AuthContext: Creating virtual profile for break-glass admin (bypass RLS)')
        return {
          user_id: user.id,
          email: user.email,
          status: 'approved',
          is_admin: true,
          created_at: new Date().toISOString(),
          virtual: true // Flag to indicate this is a virtual profile
        }
      }
      
      // Try Edge Function for profile creation to bypass RLS policies
      console.log('🔍 AuthContext: Attempting Edge Function profile creation...')
      try {
        const response = await authAPI.makeRequest('/admin-create-profile', {
          method: 'POST',
          body: JSON.stringify({
            user_id: user.id,
            email: user.email
          })
        })

        if (response && response.profile) {
          console.log('🔍 AuthContext: Successfully created profile via Edge Function:', response.profile)
          return response.profile
        }
      } catch (edgeFunctionError) {
        console.warn('🔍 AuthContext: Edge Function failed, proceeding to fallback:', edgeFunctionError.message)
      }
      
      // Final fallback: try direct insert (will show RLS error for debugging)
      console.log('🔍 AuthContext: Attempting fallback direct insert...')
      return await createUserProfileDirect(user)
      
    } catch (error) {
      console.error('🔍 AuthContext: Error in profile creation:', error)
      return null
    }
  }

  // Fallback direct profile creation (subject to RLS policies)
  const createUserProfileDirect = async (user) => {
    try {
      // Check if Supabase is available for database operations
      if (!supabase) {
        console.log('🔍 AuthContext: Supabase not available - profile creation disabled in production')
        
        // Return a basic profile object for the break-glass admin
        const isBreakGlassAdmin = user.email === 'stephen@seckar.com.au'
        if (isBreakGlassAdmin) {
          console.log('🔍 AuthContext: Creating virtual profile for break-glass admin')
          return {
            user_id: user.id,
            email: user.email,
            status: 'approved',
            is_admin: true,
            created_at: new Date().toISOString(),
            virtual: true // Flag to indicate this is a virtual profile
          }
        }
        return null
      }
      
      // For break-glass admin, create with admin privileges
      const isBreakGlassAdmin = user.email === 'stephen@seckar.com.au'
      
      // Direct insert (subject to RLS policies - may fail if RLS is enabled)
      console.log('🔍 AuthContext: Attempting direct profile insert (subject to RLS)...')
      const { data, error } = await supabase
        .from('user_profiles')
        .insert({
          user_id: user.id,
          email: user.email,
          status: isBreakGlassAdmin ? 'approved' : 'pending',
          is_admin: isBreakGlassAdmin
        })
        .select()
        .maybeSingle()

      if (error) {
        console.error('🔍 AuthContext: Error creating user profile (direct insert):', {
          code: error.code,
          details: error.details,
          hint: error.hint,
          message: error.message
        })
        
        // If it's an RLS policy violation, provide more info
        if (error.code === '42501') {
          console.error('🔍 AuthContext: RLS policy violation detected!')
          console.error('🔍 AuthContext: RLS is enabled on user_profiles table but direct insert attempted')
          console.error('🔍 AuthContext: Need to use Edge Function with service role key for profile creation')
        }
        
        return null
      }

      console.log('🔍 AuthContext: Successfully created profile (direct insert):', data)
      return data
    } catch (error) {
      console.error('🔍 AuthContext: Exception in direct profile creation:', error)
      return null
    }
  }

  useEffect(() => {
    // Get initial session using API
    const getSession = async () => {
      try {
        console.log('🔍 AuthContext: Getting initial session via API...')
        console.log('🔍 AuthContext: Current loading state:', loading)
        console.log('🔍 AuthContext: Profile operation in progress:', profileOperationInProgress.current)
        
        const { data: { session, user }, error } = await authAPI.getSession()
        
        if (error) {
          console.error('🔍 AuthContext: Auth session error:', error)
          // Clear any bad session data
          await authAPI.signOut()
          setUser(null)
          setUserProfile(null)
          setLoading(false)
          return
        }

        console.log('🔍 AuthContext: Initial session user:', user?.email || 'No user')
        console.log('🔍 AuthContext: Setting user in state')
        if (user?.id) {
          await reconcileBrowserIdentity({
            nextUserId: user.id,
            authClient: authAPI,
            directClient: supabase,
            manager: sessionManager,
            failClosed: failClosedBrowserAuthority,
          })
          sessionManager.activateSession(session, { source: 'custom' })
        }
        clearPriorUserBeforeSwitch(user)
        setUser(user)

        if (user) {
          // Simplified: Just set user and defer profile loading
          console.log('🔍 AuthContext: User authenticated, deferring profile loading...')
          setUserProfile(null) // Will be loaded when needed
          
          // Initialize session management for authenticated users
          console.log('🔍 AuthContext: Initializing session management...')
          sessionManager.initializeSession().then(success => {
            if (success) {
              console.log('🔍 AuthContext: ✅ Session management initialized')
            } else {
              console.log('🔍 AuthContext: ❌ Session management initialization failed')
            }
          })
        } else {
          setUserProfile(null)
        }
        
        setLoading(false)
      } catch (error) {
        console.error('🔍 AuthContext: Session error:', error)
        // Clear any bad session data
        await authAPI.signOut()
        setUser(null)
        setUserProfile(null)
        setLoading(false)
      }
    }

    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      setLoading(false)
    }, 3000)

    getSession().finally(() => {
      clearTimeout(timeoutId)
    })

    // Listen for auth changes using API client
    const authStateSubscription = authAPI.onAuthStateChange(
      async (event, session) => {
        console.log('🔍 AuthContext: Auth state changed via API:', event, session?.user?.email || 'No user', 'isSigningOut:', isSigningOutRef.current)
        console.log('🔍 AuthContext: Current loading state:', loading)
        console.log('🔍 AuthContext: Profile operation in progress:', profileOperationInProgress.current)
        
        // CRITICAL: If we're in the middle of signing out, handle immediately
        if (isSigningOutRef.current || event === 'SIGNED_OUT') {
          console.log('🔍 AuthContext: Processing sign-out - clearing all state immediately')
          
          // Clear profile operation if in progress
          if (profileOperationInProgress.current) {
            console.log('🔍 AuthContext: Clearing profile operation during sign-out')
            endProfileOperation('sign-out cleanup')
          }
          
          setUser(null)
          setUserProfile(null)
          setIsSigningOut(false)
          isSigningOutRef.current = false
          setLoading(false)
          
          // Clear any residual storage (handled by authAPI now)
          try {
            authAPI.clearTokens()
          } catch (e) {
            console.warn('Token cleanup failed:', e)
          }
          
          return // EXIT EARLY - don't do any profile fetching
        }
        
        // Handle normal auth events (sign-in, token refresh, etc.)
        try {
          // CRITICAL: For API-based auth, we need to extract user from session or fetch it
          let user = session?.user ?? null
          
          // If we don't have user data in session, fetch it from the current session
          if (!user && session?.access_token && event === 'SIGNED_IN') {
            console.log('🔍 AuthContext: Session has no user data, fetching from API...')
            try {
              const { data } = await authAPI.getSession()
              user = data?.user || null
              console.log('🔍 AuthContext: Fetched user from API:', user?.email || 'No user')
            } catch (fetchError) {
              console.error('🔍 AuthContext: Failed to fetch user from API:', fetchError)
            }
          }
          
          console.log('🔍 AuthContext: Setting user in auth state change:', user?.email || 'No user')
          if (user?.id) {
            await reconcileBrowserIdentity({
              nextUserId: user.id,
              authClient: authAPI,
              directClient: supabase,
              manager: sessionManager,
              failClosed: failClosedBrowserAuthority,
            })
            sessionManager.activateSession(session, { source: 'custom' })
          }
          clearPriorUserBeforeSwitch(user)
          setUser(user)

          if (user && !isSigningOutRef.current) {
            // Simplified: Just set user and defer profile loading
            console.log('🔍 AuthContext: User authenticated on auth change, deferring profile loading...')
            setUserProfile(null) // Will be loaded when needed
          } else if (!user) {
            // No user and not signing out - clear profile
            console.log('🔍 AuthContext: No user, clearing profile')
            setUserProfile(null)
          }
          
          console.log('🔍 AuthContext: Auth state change complete, setting loading to false')
          setLoading(false)
        } catch (error) {
          console.error('🔍 AuthContext: Auth state change error:', error)
          console.log('🔍 AuthContext: Error in auth state change, setting loading to false')
          setLoading(false)
        }
      }
    )

    // CRITICAL: Also listen for direct Supabase auth changes (for official MFA flow)
    let supabaseAuthSubscription = null
    if (supabase?.auth?.onAuthStateChange) {
      console.log('🔍 AuthContext: Setting up Supabase direct auth listener for MFA support...')
      supabaseAuthSubscription = supabase.auth.onAuthStateChange(
        async (event, session) => {
          console.log('🔍 AuthContext: Direct Supabase auth state changed:', event, session?.user?.email || 'No user', {
            aal: session?.aal,
            hasAccessToken: !!session?.access_token
          })
          
          // Handle successful logins from direct Supabase calls
          if (event === 'SIGNED_IN' && session?.user && session?.access_token && !isSigningOutRef.current) {
            console.log('🔍 AuthContext: Processing direct Supabase SIGNED_IN event')
            
            authAPI.saveTokensToStorage(session.access_token, session.refresh_token, session.user.id, 'direct')
            await reconcileBrowserIdentity({
              nextUserId: session.user.id,
              authClient: authAPI,
              directClient: supabase,
              directEventSession: session,
              manager: sessionManager,
              failClosed: failClosedBrowserAuthority,
            })
            sessionManager.activateSession(session, { source: 'direct' })

            // Update AuthContext with the new session
            clearPriorUserBeforeSwitch(session.user)
            setUser(session.user)
            setUserProfile(null) // Will be loaded when needed
            setLoading(false)
            
            // Clear any stale state
            setIsSigningOut(false)
            isSigningOutRef.current = false
          }
          
          // Handle MFA upgrades (AAL1 -> AAL2)
          else if (event === 'MFA_CHALLENGE_VERIFIED' && session?.user && !isSigningOutRef.current) {
            console.log('🔍 AuthContext: Processing MFA_CHALLENGE_VERIFIED event - user upgraded to AAL2')
            
            if (session.access_token) {
              authAPI.saveTokensToStorage(session.access_token, session.refresh_token, session.user.id, 'direct')
            }
            await reconcileBrowserIdentity({
              nextUserId: session.user.id,
              authClient: authAPI,
              directClient: supabase,
              directEventSession: session,
              manager: sessionManager,
              failClosed: failClosedBrowserAuthority,
            })
            sessionManager.activateSession(session, { source: 'direct' })

            // Update AuthContext with the upgraded session
            clearPriorUserBeforeSwitch(session.user)
            setUser(session.user)
            setUserProfile(null) // Will be loaded when needed  
            setLoading(false)
          }
          
          // Handle sign outs
          else if (event === 'SIGNED_OUT' || !session) {
            console.log('🔍 AuthContext: Processing direct Supabase SIGNED_OUT event')
            if (!isSigningOutRef.current && shouldPreserveCustomAuthorityOnDirectSignOut(authAPI)) {
              console.log('🔍 AuthContext: Preserving verified custom authority after stale direct identity removal')
              return
            }
            clearBrowserSessionState({ reason: 'signed-out' })
            
            // Only clear state if we're not already handling a sign out via authAPI
            if (!isSigningOutRef.current) {
              setUser(null)
              setUserProfile(null)
              setLoading(false)
            }
          }
        }
      )
    }

    return () => {
      authStateSubscription.unsubscribe()
      if (supabaseAuthSubscription?.data?.subscription) {
        supabaseAuthSubscription.data.subscription.unsubscribe()
      }
    }
  }, [])

  // Enhanced recovery mechanism for stuck states and intelligent database health monitoring
  useEffect(() => {
    const checkForStuckStates = () => {
      const connectionState = getConnectionState()
      
      console.log('🔍 AuthContext: Intelligent health monitoring check...', {
        loading,
        profileOperationInProgress: profileOperationInProgress.current,
        user: user?.email || 'No user',
        userProfile: userProfile ? 'Present' : 'Missing',
        isSigningOut: isSigningOut,
        dbConnectionHealthy,
        circuitBreakerOpen: connectionState.circuitBreakerOpen,
        consecutiveFailures: connectionState.consecutiveFailures,
        connectionAge: connectionState.age
      })
      
      // If we're not signing out and have a user but are still loading for too long, force recovery
      if (!isSigningOut && user && loading && !profileOperationInProgress.current) {
        console.warn('🔍 AuthContext: Detected stuck loading state - forcing recovery')
        setLoading(false)
      }
      
      // Intelligent health check strategy:
      // 1. Only check if connection is marked unhealthy locally
      // 2. Don't check if circuit breaker is open (it has its own recovery)
      // 3. Don't check if we're in the middle of profile operations
      // 4. Add minimum interval between checks to prevent spam
      const shouldPerformHealthCheck = !dbConnectionHealthy && 
                                     !connectionState.circuitBreakerOpen && 
                                     !profileOperationInProgress.current &&
                                     connectionState.age > 60000 // Wait at least 1 minute between checks
      
      if (shouldPerformHealthCheck) {
        console.log('🔍 AuthContext: Performing intelligent recovery health check...')
        checkDatabaseHealthWithState().catch(error => {
          console.error('🔍 AuthContext: Recovery health check failed:', error)
        })
      } else if (!dbConnectionHealthy) {
        console.log('🔍 AuthContext: Skipping health check - conditions not met or circuit breaker handling recovery')
      }
    }

    // Check for stuck states every 30 seconds (less aggressive to reduce connection pool pressure)
    const stuckStateCheckInterval = setInterval(checkForStuckStates, 30000)

    return () => {
      clearInterval(stuckStateCheckInterval)
    }
  }, [loading, user, userProfile, isSigningOut, dbConnectionHealthy])

  const signUp = async (email, password) => {
    console.log('🔍 AuthContext: Attempting signup via API for:', email)
    const { data, error } = await authAPI.signUp(email, password)
    
    if (error) {
      console.error('🔍 AuthContext: API signup error:', error)
    } else {
      console.log('🔍 AuthContext: API signup successful for:', email)
    }
    
    return { data, error }
  }

  const signIn = async (email, password) => {
    try {
      console.log('🔍 AuthContext: Attempting sign in via API for:', email)
      const { data, error } = await authAPI.signInWithPassword(email, password)
      
      if (error) {
        console.error('🔍 AuthContext: API sign in error:', error)
      } else if (data?.mfa_required) {
        // Return MFA required state - UI will handle the verification modal
        return { 
          data: { 
            mfa_required: true, 
            email,
            factors: data.factors,
            mfa_token: data.mfa_token 
          }, 
          error: null 
        }
      } else {
        console.log('🔍 AuthContext: API sign in successful for:', email)
      }
      
      return { data, error }
    } catch (error) {
      console.error('🔍 AuthContext: Sign in exception:', error)
      return { data: null, error }
    }
  }

  const signInWithMfa = async (email, password, code) => {
    try {
      const { data, error } = await authAPI.signInWithMfa(email, password, code)
      return { data, error }
    } catch (error) {
      console.error('🔍 AuthContext: MFA sign in exception:', error)
      return { data: null, error }
    }
  }

  const signOut = async () => {
    try {
      console.log('🔍 AuthContext: Starting API sign out process...')
      
      // CRITICAL: Set signing out flag FIRST to prevent profile fetching
      setIsSigningOut(true)
      isSigningOutRef.current = true
      
      // Add emergency timeout - if sign out takes more than 3 seconds, force it
      const emergencyTimeout = setTimeout(() => {
        console.error('🔍 AuthContext: Sign out emergency timeout - forcing logout')
        setUser(null)
        setUserProfile(null)
        setIsSigningOut(false)
        isSigningOutRef.current = false
        setLoading(false)
        
        // Clear all possible auth storage via authAPI
        try {
          authAPI.clearTokens()
        } catch (e) {
          console.warn('Token clear failed:', e)
        }
        
        // Force page reload as ultimate fallback
        setTimeout(() => window.location.reload(), 100)
      }, 3000)
      
      // Call API sign out
      const { error } = await authAPI.signOut()
      
      // Clear the emergency timeout since we got a response
      clearTimeout(emergencyTimeout)
      
      if (error) {
        console.error('🔍 AuthContext: API sign out error:', error)
        setIsSigningOut(false)
        isSigningOutRef.current = false
        return { error }
      }
      
      // SUCCESS: Clear states immediately since API doesn't have real-time events
      console.log('🔍 AuthContext: API sign out successful, clearing states...')
      
      // Cleanup session manager
      try {
        await terminateBrowserAuthority({
          authClient: authAPI,
          directClient: supabase,
          manager: sessionManager,
          failClosed: failClosedBrowserAuthority,
        })
        console.log('🔍 AuthContext: ✅ Session manager cleanup complete')
      } catch (e) {
        console.warn('🔍 AuthContext: Session manager cleanup failed:', e)
      }
      
      setUser(null)
      setUserProfile(null)
      setIsSigningOut(false)
      isSigningOutRef.current = false
      setLoading(false)
      
      return { error: null }
      
    } catch (error) {
      console.error('🔍 AuthContext: Sign out exception:', error)
      setIsSigningOut(false)
      isSigningOutRef.current = false
      return { error }
    }
  }

  const resetPassword = async (email) => {
    try {
      console.log('🔍 AuthContext: Password reset not yet implemented in API')
      // Note: This would need a corresponding API endpoint
      const { data, error } = await authAPI.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/?type=recovery`
      })
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  }

  const updatePassword = async (newPassword) => {
    try {
      console.log('🔍 AuthContext: Password update not yet implemented in API')
      // Note: This would need a corresponding API endpoint
      const { data, error } = await authAPI.updateUser({
        password: newPassword
      })
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  }

  // Helper function to verify session validity before admin operations
  const verifySessionForAdminOperation = async (operationName) => {
    console.log(`🔍 AuthContext: Verifying session validity for ${operationName}...`)
    console.log(`🔍 AuthContext: Current user state:`, { 
      userEmail: user?.email || 'No user', 
      hasUserProfile: !!userProfile,
      loading 
    })
    
    const { data: { session }, error: sessionError } = await authAPI.getSession()
    
    console.log(`🔍 AuthContext: Session check result for ${operationName}:`, {
      hasSession: !!session,
      hasAccessToken: !!session?.access_token,
      hasRefreshToken: !!session?.refresh_token,
      sessionError: sessionError?.message || 'None'
    })
    
    if (sessionError || !session || !session.access_token) {
      console.error(`🔍 AuthContext: No valid session found for ${operationName}:`, sessionError?.message || 'Session or access_token missing')
      
      // CRITICAL: Check if this is a network error vs authentication error
      const isNetworkIssue = sessionError?.isNetworkError || 
                           sessionError?.message?.includes('Network connectivity issue') ||
                           sessionError?.message?.includes('Failed to fetch')
      
      if (isNetworkIssue) {
        console.log(`🔍 AuthContext: Network error detected for ${operationName} - attempting graceful degradation`)
        
        // For network errors, try to continue with cached session if available
        if (session?.access_token) {
          console.log(`🔍 AuthContext: Using cached session for ${operationName} despite network issues`)
          return { 
            success: true, 
            session,
            warning: 'Operating in offline mode due to network connectivity issues'
          }
        } else {
          // No cached session available
          return { 
            success: false, 
            error: { 
              message: 'Network connectivity issues. Please check your connection and try again.',
              isNetworkError: true
            } 
          }
        }
      }
      
      // For authentication errors (not network), attempt session refresh
      console.log(`🔍 AuthContext: Attempting session refresh for ${operationName}...`)
      try {
        const { data: { session: refreshedSession }, error: refreshError } = await authAPI.refreshSession()
        
        if (!refreshError && refreshedSession?.access_token) {
          console.log(`🔍 AuthContext: Session refresh successful for ${operationName}`)
          return { success: true, session: refreshedSession }
        } else {
          console.error(`🔍 AuthContext: Session refresh failed for ${operationName}:`, refreshError?.message || 'No refreshed session')
          
          // Only clear session state on actual authentication failures
          if (!refreshError?.isNetworkError) {
            setUser(null)
            setUserProfile(null)
          }
          
          return { 
            success: false, 
            error: { 
              message: refreshError?.isNetworkError 
                ? 'Network connectivity issues. Please check your connection and try again.'
                : 'Session expired. Please log in again.',
              isNetworkError: refreshError?.isNetworkError || false
            } 
          }
        }
      } catch (refreshException) {
        console.error(`🔍 AuthContext: Session refresh exception for ${operationName}:`, refreshException.message)
        
        // Only clear state if it's not a network error
        const isRefreshNetworkError = refreshException.message?.includes('Network connectivity issue') ||
                                     refreshException.message?.includes('Failed to fetch')
        
        if (!isRefreshNetworkError) {
          setUser(null)
          setUserProfile(null)
        }
        
        return { 
          success: false, 
          error: { 
            message: isRefreshNetworkError 
              ? 'Network connectivity issues. Please check your connection and try again.'
              : 'Authentication failed. Please log in again.',
            isNetworkError: isRefreshNetworkError
          } 
        }
      }
    } else {
      console.log(`🔍 AuthContext: Valid session confirmed for ${operationName}`)
      return { success: true, session }
    }
  }

  // Admin functions - using static admin endpoint for reliability
  const getAllUsers = async () => {
    try {
      console.log('🔍 AuthContext: Fetching all users via centralized admin API')
      
      // Get current session
      const { data: { session } } = await authAPI.getSession()
      if (!session?.access_token) {
        return { data: null, error: { message: 'No valid session found' } }
      }
      
      // Use centralized admin API with Supabase backend
      const responseData = await adminAPI.listUsers(session.access_token)
      console.log(`🔍 AuthContext: Successfully fetched ${responseData.count || 0} users via centralized admin API`)
      
      return { data: responseData.users || [], error: null }
    } catch (error) {
      console.error('🔍 AuthContext: Exception fetching users via centralized admin API:', error)
      return { data: null, error: { message: error.message } }
    }
  }

  const approveUser = async (userId) => {
    try {
      console.log(`🔍 AuthContext: Approving user ${userId} via centralized admin API`)
      
      // Get current session
      const { data: { session } } = await authAPI.getSession()
      if (!session?.access_token) {
        return { data: null, error: { message: 'No valid session found' } }
      }
      
      // Use centralized admin API with Supabase backend
      const responseData = await adminAPI.approveUser(userId, session.access_token)
      console.log('🔍 AuthContext: Successfully approved user via centralized admin API:', responseData)
      
      return { data: responseData, error: null }
    } catch (error) {
      console.error('🔍 AuthContext: Exception approving user via centralized admin API:', error)
      return { data: null, error: { message: error.message } }
    }
  }

  const rejectUser = async (userId) => {
    try {
      console.log(`🔍 AuthContext: Rejecting user ${userId} via centralized admin API`)
      
      // Get current session
      const { data: { session } } = await authAPI.getSession()
      if (!session?.access_token) {
        return { data: null, error: { message: 'No valid session found' } }
      }
      
      // Use centralized admin API with Supabase backend
      // Note: rejectUser uses the same delete endpoint as deleteUser
      const responseData = await adminAPI.deleteUser(userId, session.access_token)
      console.log('🔍 AuthContext: Successfully rejected user via centralized admin API:', responseData)
      
      return { data: responseData, error: null }
    } catch (error) {
      console.error('🔍 AuthContext: Exception rejecting user via centralized admin API:', error)
      return { data: null, error: { message: error.message } }
    }
  }

  const deleteUser = async (userId) => {
    try {
      console.log(`🔍 AuthContext: Deleting user ${userId} via centralized admin API`)
      
      // Get current session
      const { data: { session } } = await authAPI.getSession()
      if (!session?.access_token) {
        return { data: null, error: { message: 'No valid session found' } }
      }
      
      // STEP 1: Try Edge Function first
      try {
        const responseData = await adminAPI.deleteUser(userId, session.access_token)
        console.log('🔍 AuthContext: Edge Function deletion result:', responseData)
        
        // If Edge Function worked completely, return success
        if (responseData.complete) {
          return { 
            data: { 
              message: responseData.message,
              complete: true
            }, 
            error: null 
          }
        }
        
        // If Edge Function only deleted auth but not profile, try backup
        if (responseData.auth_deleted && !responseData.profile_deleted) {
          console.log('🔧 AuthContext: Edge Function only deleted auth, trying backup profile deletion...')
          
          // Backup: Delete profile directly via Supabase
          const profileDeleteResult = await backupDeleteProfile(userId, session.access_token)
          
          if (profileDeleteResult.success) {
            return {
              data: {
                message: 'User deleted successfully (with backup profile deletion)',
                complete: true
              },
              error: null
            }
          } else {
            return {
              data: {
                message: 'User deleted from auth, but profile deletion failed',
                complete: false,
                warning: profileDeleteResult.error
              },
              error: null
            }
          }
        }
        
        // Return whatever the Edge Function returned
        return { 
          data: responseData, 
          error: null 
        }
        
      } catch (edgeFunctionError) {
        console.error('🔍 AuthContext: Edge Function failed, trying full backup deletion:', edgeFunctionError)
        
        // STEP 2: Full backup deletion if Edge Function fails completely
        return await backupDeleteUser(userId, session.access_token)
      }
      
    } catch (error) {
      console.error('🔍 AuthContext: Exception in deleteUser:', error)
      return { data: null, error: { message: error.message } }
    }
  }

  // Backup function to delete profile directly via Supabase
  const backupDeleteProfile = async (userId, accessToken) => {
    try {
      console.log('🔧 AuthContext: Backup profile deletion for:', userId)
      
      if (!supabase) {
        return { success: false, error: 'Supabase not available' }
      }
      
      const { error } = await supabase
        .from('user_profiles')
        .delete()
        .eq('user_id', userId)
      
      if (error) {
        console.error('🔧 AuthContext: Backup profile deletion failed:', error)
        return { success: false, error: error.message }
      }
      
      console.log('✅ AuthContext: Backup profile deletion successful')
      return { success: true }
      
    } catch (error) {
      console.error('🔧 AuthContext: Exception in backup profile deletion:', error)
      return { success: false, error: error.message }
    }
  }

  // Full backup deletion function
  const backupDeleteUser = async (userId, accessToken) => {
    try {
      console.log('🔧 AuthContext: Full backup deletion for:', userId)
      
      if (!supabase) {
        return { data: null, error: { message: 'Supabase not available for backup deletion' } }
      }
      
      // Delete profile first
      const { error: profileError } = await supabase
        .from('user_profiles')
        .delete()
        .eq('user_id', userId)
      
      let profileDeleted = !profileError
      
      if (profileError) {
        console.error('🔧 AuthContext: Backup profile deletion failed:', profileError)
      } else {
        console.log('✅ AuthContext: Backup profile deletion successful')
      }
      
      // Try to delete from auth using admin client
      // Note: This requires service role access which we might not have in frontend
      // But the Edge Function should have handled this part
      
      return {
        data: {
          message: profileDeleted ? 'Profile deleted via backup method' : 'Backup deletion had issues',
          complete: profileDeleted,
          backup_used: true,
          profile_deleted: profileDeleted,
          auth_deleted: false, // We can't delete auth from frontend
          warning: profileDeleted ? null : 'Could not delete user profile via backup method'
        },
        error: null
      }
      
    } catch (error) {
      console.error('🔧 AuthContext: Exception in backup deletion:', error)
      return { data: null, error: { message: `Backup deletion failed: ${error.message}` } }
    }
  }

  // Enhanced admin detection with auth-based RLS support
  const isBreakGlassAdmin = user?.email === 'stephen@seckar.com.au'
  const isProfileAdmin = userProfile?.is_admin === true
  
  // Auth-based RLS admin detection: break-glass admin is always admin, even without profile
  // Profile admin status comes from user_profiles.is_admin (which should mirror admin_roles)
  const isAdmin = isBreakGlassAdmin || isProfileAdmin
  
  // EMAIL-BASED APPROVAL: Users are approved automatically after email confirmation
  // This bypasses the need for manual admin approval
  const isEmailConfirmed = user?.email_confirmed_at != null
  const isApproved = isEmailConfirmed || isBreakGlassAdmin
  const isPending = !isEmailConfirmed && !isBreakGlassAdmin

  console.log('🔍 AuthContext: Admin status calculation:', {
    userEmail: user?.email,
    isBreakGlassAdmin,
    isProfileAdmin,
    finalIsAdmin: isAdmin,
    isEmailConfirmed,
    isApproved,
    isPending,
    profileStatus: userProfile?.status,
    hasUserProfile: !!userProfile,
    dbConnectionHealthy,
    loading
  })

  // Auth-based RLS admin verification function
  const verifyAdminStatusFromRoles = async (userId) => {
    try {
      console.log('🔍 AuthContext: Verifying admin status from admin_roles table...')
      
      const queryFn = () => supabase
        .from('admin_roles')
        .select('is_admin, granted_at, revoked_at, notes')
        .eq('user_id', userId)
        .single()

      const { data, error } = await executeQueryWithState(
        queryFn, 
        'verifyAdminStatus',
        { 
          retries: 1,
          retryDelay: 1000
        }
      )

      if (error) {
        if (error.code === 'PGRST116') {
          console.log('🔍 AuthContext: No admin_roles entry found - user is not admin')
          return { isAdmin: false, reason: 'No admin_roles entry' }
        }
        console.error('🔍 AuthContext: Error verifying admin status:', error)
        return { isAdmin: false, reason: 'Query error', error }
      }

      const isActiveAdmin = data.is_admin === true && 
                           (data.revoked_at === null || new Date(data.revoked_at) > new Date())

      console.log('🔍 AuthContext: Admin status verification result:', {
        isAdmin: data.is_admin,
        isActiveAdmin,
        grantedAt: data.granted_at,
        revokedAt: data.revoked_at,
        notes: data.notes
      })

      return { 
        isAdmin: isActiveAdmin, 
        details: data,
        reason: isActiveAdmin ? 'Active admin' : 'Admin privileges revoked'
      }
    } catch (error) {
      console.error('🔍 AuthContext: Exception verifying admin status:', error)
      return { isAdmin: false, reason: 'Exception', error }
    }
  }

  // Comprehensive diagnostics function
  const runDiagnostics = async () => {
    console.log('🔍 AuthContext: Running comprehensive diagnostics...')
    
    const diagnostics = {
      timestamp: new Date().toISOString(),
      authMode: 'API-only (Production Mode)',
      supabaseConfig: {
        available: false,
        reason: 'Running in API-only mode - Supabase credentials not available in frontend'
      },
      tests: {}
    }

    // Test 1: Basic Network Connectivity
    try {
      console.log('🔍 AuthContext: Test 1 - Basic network connectivity...')
      const response = await fetch('https://www.google.com/favicon.ico', { 
        method: 'HEAD',
        mode: 'no-cors'
      })
      diagnostics.tests.networkConnectivity = { status: 'pass', message: 'Internet connectivity OK' }
    } catch (error) {
      diagnostics.tests.networkConnectivity = { status: 'fail', error: error.message }
    }

    // Test 2: API Endpoints (our secure authentication API)
    try {
      console.log('🔍 AuthContext: Test 2 - E8 Tracker API health...')
      const response = await fetch('/api/shared', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      
      const data = response.ok ? await response.json() : null
      diagnostics.tests.e8TrackerAPI = { 
        status: response.ok ? 'pass' : 'fail', 
        httpStatus: response.status,
        message: response.ok ? 'API endpoints accessible' : `API returned ${response.status}`,
        data: data
      }
    } catch (error) {
      diagnostics.tests.e8TrackerAPI = { status: 'fail', error: error.message }
    }

    // Test 3: Authentication API endpoints
    try {
      console.log('🔍 AuthContext: Test 3 - Authentication API health...')
      const response = await fetch('/api/auth-session', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer invalid-token-for-test'
        }
      })
      
      // We expect a 401 response which means the endpoint is working
      diagnostics.tests.authAPI = { 
        status: response.status === 401 ? 'pass' : 'fail', 
        httpStatus: response.status,
        message: response.status === 401 ? 'Auth API responding correctly' : `Unexpected auth API response: ${response.status}`
      }
    } catch (error) {
      diagnostics.tests.authAPI = { status: 'fail', error: error.message }
    }

    // Test 4: Direct Database Health Check (bypasses circuit breaker)
    try {
      console.log('🔍 AuthContext: Test 4 - Direct database query...')
      const isHealthy = await checkConnectionHealthDirect()
      diagnostics.tests.databaseHealthDirect = { 
        status: isHealthy ? 'pass' : 'fail', 
        message: isHealthy ? 'Direct database queries working' : 'Direct database queries failed'
      }
    } catch (error) {
      diagnostics.tests.databaseHealthDirect = { status: 'fail', error: error.message }
    }

    // Test 5: Circuit Breaker Status (before potential reset)
    try {
      const connectionState = getConnectionState()
      diagnostics.tests.circuitBreaker = {
        status: connectionState.circuitBreakerOpen ? 'open' : 'closed',
        consecutiveFailures: connectionState.consecutiveFailures,
        lastError: connectionState.lastError?.message,
        healthyConnection: connectionState.healthy,
        age: connectionState.age
      }
    } catch (error) {
      diagnostics.tests.circuitBreaker = { status: 'error', error: error.message }
    }

    // Test 6: Circuit Breaker Protected Database Health Check
    try {
      console.log('🔍 AuthContext: Test 6 - Circuit breaker protected database query...')
      await checkDatabaseHealthWithState()
      diagnostics.tests.databaseHealthProtected = { status: 'pass', message: 'Protected database queries working' }
    } catch (error) {
      diagnostics.tests.databaseHealthProtected = { status: 'fail', error: error.message }
    }

    console.log('🔍 AuthContext: Diagnostics complete:', diagnostics)
    return diagnostics
  }

  // Circuit breaker management functions
  const resetConnectionState = () => {
    try {
      const newState = resetCircuitBreaker()
      setDbConnectionHealthy(true)
      console.log('🔍 AuthContext: Circuit breaker reset successful:', newState)
      return { success: true, state: newState }
    } catch (error) {
      console.error('🔍 AuthContext: Circuit breaker reset failed:', error)
      return { success: false, error: error.message }
    }
  }

  const value = {
    user,
    userProfile,
    signUp,
    signIn,
    signInWithMfa,
    signOut,
    resetPassword,
    updatePassword,
    loading,
    isSigningOut,
    isAdmin,
    isApproved,
    isPending,
    getAllUsers,
    approveUser,
    rejectUser,
    deleteUser,
    dbConnectionHealthy,
    checkDatabaseHealth: checkDatabaseHealthWithState,
    runDiagnostics,
    getConnectionState, // Export connection state for debugging
    getConnectionMetrics, // Export detailed connection metrics
    resetConnectionState, // Export circuit breaker reset function
    checkConnectionHealthDirect, // Export direct health check for diagnostics
    testRawSupabaseClient, // Export raw client test function
    ensureUserProfile, // Export profile lazy loading function
    verifyAdminStatusFromRoles // Export auth-based admin verification function
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
